import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';

import type { AgentRuntime, ReceptionistSettings } from '../../../api/agentRuntimeSchemas';
import { pairWhatsApp } from '../../../api/endpoints/agentRuntime';
import { ErrorBanner } from '../../../components/ErrorBanner';
import { QrCode } from './QrCode';
import { errorText, useSaveReceptionist, type SetupStep } from './useReception';

const TIME = new Intl.DateTimeFormat('es-PA', { hour: 'numeric', minute: '2-digit' });

type StepProps = { n: number; title: string; done: boolean; open: boolean; onOpen: () => void; children: ReactNode };

function Step({ n, title, done, open, onOpen, children }: StepProps) {
  return (
    <li className="reception-step" data-done={done || undefined} data-open={open || undefined}>
      <span className="reception-step-n" aria-hidden="true">{done ? '✓' : n}</span>
      <div className="reception-step-body">
        <h3>
          <button type="button" className="reception-step-toggle" aria-expanded={open} onClick={onOpen}>
            {title}
            {done ? <span className="sr-only"> (listo)</span> : null}
          </button>
        </h3>
        {open ? children : null}
      </div>
    </li>
  );
}

function WhatsAppStep({ runtime }: { runtime: AgentRuntime | undefined }) {
  const pairing = useMutation({ mutationFn: pairWhatsApp });
  const linked = runtime?.status.whatsapp === 'linked';
  if (linked) return <p className="row-sub">Listo: Luna contesta desde el WhatsApp del negocio.</p>;
  return (
    <div className="reception-pair">
      <p>En el teléfono del negocio abre WhatsApp, entra a Dispositivos vinculados y escanea el código. Tu WhatsApp sigue funcionando igual.</p>
      {pairing.data ? (
        <div className="reception-qr-box">
          <QrCode value={pairing.data.qr} />
          <p className="row-sub">Si vence, pide otro (vale hasta las {TIME.format(new Date(pairing.data.expiresAt))}).</p>
        </div>
      ) : null}
      {pairing.error ? <ErrorBanner message={errorText(pairing.error, 'No pudimos pedir el código. Intenta de nuevo.')} /> : null}
      <button type="button" className="btn" disabled={pairing.isPending} onClick={() => pairing.mutate()}>
        {pairing.isPending ? 'Pidiendo el código…' : pairing.data ? 'Pedir otro código' : 'Mostrar el código QR'}
      </button>
    </div>
  );
}

export function SettingsForm({ settings, onSaved }: { settings: ReceptionistSettings; onSaved?: () => void }) {
  const save = useSaveReceptionist();
  const [instructions, setInstructions] = useState(settings.instructions);
  const [tone, setTone] = useState(settings.tone);
  const [canBook, setCanBook] = useState(settings.canBook);
  const [handoffPhone, setHandoffPhone] = useState(settings.handoffPhone ?? '');
  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    save.mutate({ instructions: instructions.trim(), tone: tone.trim(), canBook, handoffPhone: handoffPhone.trim() || null }, { onSuccess: onSaved });
  }
  return (
    <form className="form-grid reception-form" onSubmit={handleSubmit}>
      <label className="field">
        <span>Qué debe saber Luna de tu negocio</span>
        <textarea rows={4} value={instructions} onChange={(event) => setInstructions(event.target.value)} placeholder="Ejemplo: los sábados solo atendemos con cita; el parqueo es gratis en el sótano." />
      </label>
      <label className="field">
        <span>Cómo habla</span>
        <input type="text" value={tone} onChange={(event) => setTone(event.target.value)} placeholder="Cercano y breve, de tú" />
      </label>
      <label className="field field-check">
        <input type="checkbox" checked={canBook} onChange={(event) => setCanBook(event.target.checked)} />
        <span>Puede reservar sola cuando hay espacio. Si lo apagas, ofrece horarios y tú confirmas.</span>
      </label>
      <label className="field">
        <span>A quién pasa la conversación cuando hace falta una persona</span>
        <input type="tel" inputMode="tel" value={handoffPhone} onChange={(event) => setHandoffPhone(event.target.value)} placeholder="6000-0000" />
      </label>
      {save.error ? <ErrorBanner message={errorText(save.error, 'No pudimos guardar. Intenta de nuevo.')} /> : null}
      {save.data && !save.data.synced ? <p className="notice">Guardado. Luna lo toma en cuanto se conecte.</p> : null}
      <div>
        <button type="submit" className="btn" disabled={save.isPending}>
          {save.isPending ? 'Guardando…' : 'Guardar'}
        </button>
      </div>
    </form>
  );
}

type SetupProps = {
  runtime: AgentRuntime | undefined;
  settings: ReceptionistSettings;
  progress: Record<SetupStep, boolean>;
};

/** Three steps, one open at a time: the first one not done yet, or whichever the owner opens. */
export function SetupSteps({ runtime, settings, progress }: SetupProps) {
  const order: SetupStep[] = ['whatsapp', 'settings', 'try'];
  const [picked, setPicked] = useState<SetupStep | null>(null);
  const current = picked ?? order.find((step) => !progress[step]) ?? 'try';
  const stepProps = (step: SetupStep) => ({ done: progress[step], open: current === step, onOpen: () => setPicked(step) });
  return (
    <ol className="reception-steps">
      <Step n={1} title="Conecta el WhatsApp del negocio" {...stepProps('whatsapp')}>
        <WhatsAppStep runtime={runtime} />
      </Step>
      <Step n={2} title="Cuéntale cómo atiendes" {...stepProps('settings')}>
        <SettingsForm settings={settings} />
      </Step>
      <Step n={3} title="Pruébala" {...stepProps('try')}>
        <p>Desde otro teléfono, escríbele al WhatsApp del negocio: “Hola, ¿tienen espacio mañana en la tarde?”. La conversación aparece en Bandeja.</p>
      </Step>
    </ol>
  );
}
